"use client";

import { useEffect, useRef, useState } from "react";
import { ADS_CLIENT } from "@/components/ads/config";
import styles from "./BaseMobileAdSensePlacement.module.css";
import { useNearViewportAd } from "@/components/ads/useNearViewportAd";

declare global {
  interface Window { adsbygoogle?: unknown[]; }
}

type Props = {
  slot: "6698802456" | "7597696012";
  position: "top" | "after-description";
};

/** Mobile-only AdSense units. Never initialise a hidden desktop ad. */
export default function BaseMobileAdSensePlacement({ slot, position }: Props) {
  const [mobile, setMobile] = useState(false);
  const { containerRef, ready } = useNearViewportAd(mobile, position === "top");
  const adRef = useRef<HTMLModElement>(null);
  const initialized = useRef(false);

  useEffect(() => {
    const query = window.matchMedia("(max-width: 767px)");
    const update = () => setMobile(query.matches);
    update();
    query.addEventListener("change", update);
    return () => query.removeEventListener("change", update);
  }, []);

  useEffect(() => {
    if (!ready || !ADS_CLIENT || !adRef.current || initialized.current) return;
    if (adRef.current.getAttribute("data-adsbygoogle-status")) {
      initialized.current = true;
      return;
    }
    try {
      (window.adsbygoogle = window.adsbygoogle || []).push({});
      initialized.current = true;
    } catch (error) {
      console.warn(`AdSense mobile placement ${slot} initialization failed:`, error);
    }
  }, [ready, slot]);

  if (!mobile || !ADS_CLIENT) return null;

  return (
    <div
      ref={containerRef}
      className={`${styles.breakout} ${position === "top" ? styles.top : styles.afterDescription}`}
      aria-label="Advertisement"
      data-bfc-adsense-mobile-slot={slot}
    >
      {ready ? <ins
        ref={adRef}
        className="adsbygoogle"
        style={{ display: "block", width: "100%" }}
        data-ad-client="ca-pub-7902170378611353"
        data-ad-slot={slot}
        data-ad-format="auto"
        data-full-width-responsive="true"
      /> : null}
    </div>
  );
}
